import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, IconButton, MenuItem, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import { GripVertical, LocateFixed, Users } from 'lucide-react';
import { FloorplanAssignment, FloorplanEmployee, FloorplanLevel } from '../../types/floorplan';
import { EmptyState, PersonCell, SearchField, SectionCard, SegmentedControl, StatusPill, useDelayedFlag } from '../ui';
import { sbg, transition } from '../../theme/sbgTokens';
import { TeamDot } from './teamColors';
import { formatCount, formatNumber } from '../../utils/format';
import { deskInSentence, deskPlaceLabel, floorNamesWhenMixed } from './floorplan-panels/deskLabel';

interface EmployeeRosterProps {
  employees: FloorplanEmployee[];
  assignmentsByEmployeeId: Map<string, FloorplanAssignment>;
  /** Transient: a save is in flight or there is nothing to drag into. Dims the grips; doesn't change the copy. */
  disabled?: boolean;
  /**
   * Optional: whether this person may assign desks at all (permission + a version to assign into).
   * Drives the card description so it doesn't flicker while a save is in flight.
   * Defaults to "any roster row is editable".
   */
  canAssign?: boolean;
  /** Optional: the layout's floors, so a desk on a multi-floor layout reads "Desk 54 · 4th Floor". */
  levels?: FloorplanLevel[];
  /**
   * Optional: desk assignments haven't loaded yet. Names still show, but the
   * "without a desk" count, the per-person desk pills and the desk-status
   * filter wait for them (a neutral placeholder instead of "No desk").
   */
  assignmentsLoading?: boolean;
  /** Optional: where people are added (pass '/employees' for admin/HR). The first-use empty state links there. */
  employeesHref?: string;
  onDragStart: (event: React.DragEvent, employee: FloorplanEmployee) => void;
  onLocateEmployee: (employee: FloorplanEmployee) => void;
}

type AssignmentFilter = 'all' | 'assigned' | 'unassigned';

const ASSIGNMENT_OPTIONS: { value: AssignmentFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'unassigned', label: 'Unassigned' },
];

/* The roster sits in a 308px rail on wide screens and full width below lg, so
   its layout follows its own width (container query), not the viewport. */
const ROSTER_CONTAINER = 'floorplan-roster';

const EmployeeRoster: React.FC<EmployeeRosterProps> = ({
  employees,
  assignmentsByEmployeeId,
  disabled = false,
  canAssign,
  levels,
  assignmentsLoading = false,
  employeesHref,
  onDragStart,
  onLocateEmployee,
}) => {
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('all');

  const teams = useMemo(
    () =>
      Array.from(new Set(employees.map((employee) => employee.teamName).filter(Boolean) as string[]))
        .sort((left, right) => left.localeCompare(right)),
    [employees]
  );
  const teamColors = useMemo(() => {
    const colors = new Map<string, string>();
    employees.forEach((employee) => {
      if (employee.teamName && !colors.has(employee.teamName)) colors.set(employee.teamName, employee.teamColor);
    });
    return colors;
  }, [employees]);

  const query = search.trim().toLowerCase();
  // Until assignments are known, "Assigned"/"Unassigned" can't be answered, so the filter reads as "All".
  const deskFilter: AssignmentFilter = assignmentsLoading ? 'all' : assignmentFilter;
  const showDeskPlaceholders = useDelayedFlag(assignmentsLoading);

  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const assignment = assignmentsByEmployeeId.get(employee.id);
      const matchesSearch = !query
        || employee.name.toLowerCase().includes(query)
        || employee.email.toLowerCase().includes(query)
        || (employee.teamName || '').toLowerCase().includes(query)
        || (employee.department?.name || '').toLowerCase().includes(query);
      const matchesTeam = teamFilter === 'all' || employee.teamName === teamFilter;
      const matchesAssignment = deskFilter === 'all'
        || (deskFilter === 'assigned' && Boolean(assignment))
        || (deskFilter === 'unassigned' && !assignment);

      return matchesSearch && matchesTeam && matchesAssignment;
    });
  }, [deskFilter, assignmentsByEmployeeId, employees, query, teamFilter]);

  const unassignedCount = useMemo(
    () => employees.filter((employee) => !assignmentsByEmployeeId.has(employee.id)).length,
    [assignmentsByEmployeeId, employees]
  );
  const floorNames = useMemo(() => floorNamesWhenMixed(levels), [levels]);
  const filtersActive = query !== '' || teamFilter !== 'all' || deskFilter !== 'all';
  // Permission, not the transient saving flag, decides the hint, so it doesn't flicker on every save.
  const canDragAny = canAssign ?? employees.some((employee) => employee.canEdit);
  const clearFilters = () => {
    setSearch('');
    setTeamFilter('all');
    setAssignmentFilter('all');
  };

  const deskStatusOptions = assignmentsLoading
    ? ASSIGNMENT_OPTIONS.map((option) => (option.value === 'all' ? option : { ...option, disabled: true }))
    : ASSIGNMENT_OPTIONS;

  let countLabel: React.ReactNode;
  if (filtersActive) {
    countLabel = `Showing ${formatNumber(filteredEmployees.length)} of ${formatNumber(employees.length)}`;
  } else if (assignmentsLoading) {
    // The headcount is known; how many lack a desk isn't until assignments load.
    countLabel = (
      <>
        {formatCount(employees.length, 'employee')}
        <span className="sbg-sr-only">, desk assignments loading</span>
        <Skeleton
          aria-hidden="true"
          variant="rounded"
          animation={false}
          width={96}
          height={12}
          sx={{ display: 'inline-block', verticalAlign: 'middle', ml: 1, visibility: showDeskPlaceholders ? 'visible' : 'hidden' }}
        />
      </>
    );
  } else {
    countLabel = `${formatCount(employees.length, 'employee')} · ${formatNumber(unassignedCount)} without a desk`;
  }

  return (
    <SectionCard
      dense
      flush
      id="floorplan-roster"
      title="Roster"
      description={canDragAny ? 'Drag a name onto a desk to assign it.' : 'Find where each person sits.'}
      sx={{ height: '100%', containerType: 'inline-size', containerName: ROSTER_CONTAINER }}
    >
      {employees.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${sbg.border.subtle}`,
            // One row once there is room for all three filters; stacked in the side rail.
            [`@container ${ROSTER_CONTAINER} (min-width: 640px)`]: {
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 200px) auto',
              alignItems: 'end',
            },
          }}
        >
          <SearchField
            value={search}
            onChange={setSearch}
            label="Search roster"
            placeholder="Name, email, or team"
            width="100%"
          />
          <TextField
            select
            fullWidth
            label="Team"
            value={teamFilter}
            onChange={(event) => setTeamFilter(event.target.value)}
          >
            <MenuItem value="all">All teams</MenuItem>
            {teams.map((team) => (
              <MenuItem key={team} value={team}>
                <TeamDot color={teamColors.get(team)} />
                {team}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'grid', justifyItems: 'start', minWidth: 0 }}>
            {/* Visible label to match Team; the group's accessible name uses the same words. */}
            <Typography variant="labelMd" component="span" aria-hidden="true" sx={{ color: sbg.text.primary, mb: '6px' }}>
              Desk status
            </Typography>
            <SegmentedControl
              size="small"
              aria-label="Desk status"
              value={deskFilter}
              onChange={setAssignmentFilter}
              options={deskStatusOptions}
            />
          </Box>
        </Box>
      )}

      {employees.length > 0 && (
        <Typography
          variant="caption"
          component="p"
          aria-live="polite"
          sx={{ px: 2, pt: 1.25, pb: 0.5, color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
        >
          {countLabel}
        </Typography>
      )}

      <Box
        component="ul"
        aria-label="Floorplan employee roster"
        aria-busy={assignmentsLoading || undefined}
        sx={{
          listStyle: 'none',
          m: 0,
          px: 1,
          pt: 0.5,
          pb: 1,
          maxHeight: 'min(760px, 70vh)',
          overflowY: 'auto',
          display: 'grid',
          alignContent: 'start',
          gap: 0.25,
          [`@container ${ROSTER_CONTAINER} (min-width: 560px)`]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: 1,
          },
        }}
      >
        {filteredEmployees.map((employee) => {
          const assignment = assignmentsByEmployeeId.get(employee.id);
          const draggable = !disabled && Boolean(employee.canEdit);
          const teamLabel = employee.teamName || employee.department?.name || 'No team';
          // When the search matched the email and not the name, show the email so the match is visible.
          const showEmail = Boolean(query)
            && !employee.name.toLowerCase().includes(query)
            && employee.email.toLowerCase().includes(query);
          const lead = showEmail ? employee.email : teamLabel;
          const place = assignment ? deskPlaceLabel(assignment.desk, floorNames.get(assignment.desk.levelId)) : null;

          return (
            <Box
              component="li"
              key={employee.id}
              draggable={draggable}
              onDragStart={(event: React.DragEvent) => {
                if (employee.canEdit) {
                  onDragStart(event, employee);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                minHeight: 52,
                px: 1,
                py: 0.75,
                borderRadius: sbg.radius.md,
                cursor: draggable ? 'grab' : 'default',
                userSelect: employee.canEdit ? 'none' : 'auto',
                WebkitUserSelect: employee.canEdit ? 'none' : 'auto',
                transition: transition('background-color'),
                '&:hover': { bgcolor: sbg.bg.hover },
                '&:active': { cursor: draggable ? 'grabbing' : 'default' },
              }}
            >
              {employee.canEdit && (
                <Box
                  aria-hidden="true"
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 14,
                    flex: 'none',
                    color: sbg.text.tertiary,
                    opacity: disabled ? 0.4 : 1,
                  }}
                >
                  <GripVertical size={14} />
                </Box>
              )}
              <PersonCell
                name={employee.name}
                sx={{ flex: 1 }}
                secondary={
                  // Having a desk is the normal state, so it reads as plain text; only "No desk" gets a pill.
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    {!showEmail && employee.teamName && <TeamDot color={employee.teamColor} />}
                    <Box
                      component="span"
                      sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {place ? `${lead} · ${place}` : lead}
                    </Box>
                    {!assignment && (assignmentsLoading ? (
                      // Desk not known yet: a quiet placeholder where the pill will be, never "No desk".
                      <Skeleton
                        aria-hidden="true"
                        variant="rounded"
                        animation={false}
                        width={52}
                        height={18}
                        sx={{ flex: 'none', borderRadius: sbg.radius.pill, visibility: showDeskPlaceholders ? 'visible' : 'hidden' }}
                      />
                    ) : (
                      <StatusPill tone="neutral">No desk</StatusPill>
                    ))}
                  </Box>
                }
              />
              {assignment && (
                <Tooltip title="Locate on floor plan">
                  <IconButton
                    size="small"
                    aria-label={`Locate ${employee.name} at ${deskInSentence(assignment.desk)}`}
                    onClick={() => onLocateEmployee(employee)}
                    sx={{ flex: 'none' }}
                  >
                    <LocateFixed size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}

        {employees.length === 0 && (
          <Box component="li" sx={{ gridColumn: '1 / -1' }}>
            <EmptyState
              compact
              icon={<Users />}
              title="No one on the roster yet"
              body="People appear here once they’re added on the Employees page."
              action={
                employeesHref ? (
                  <Button variant="outlined" size="small" component={RouterLink} to={employeesHref}>
                    Go to Employees
                  </Button>
                ) : undefined
              }
            />
          </Box>
        )}

        {employees.length > 0 && filteredEmployees.length === 0 && (
          <Box component="li" sx={{ gridColumn: '1 / -1' }}>
            <EmptyState
              compact
              title="No employees match these filters"
              body={
                query
                  ? `No one matches “${search.trim()}” with the current team and desk filters.`
                  : 'No one has this team and desk status.'
              }
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          </Box>
        )}
      </Box>
    </SectionCard>
  );
};

export default EmployeeRoster;
